/**
 * React hook for Server-Sent Events (SSE) real-time notifications.
 * 
 * Usage:
 * ```tsx
 * import { useSSE } from '../hooks/useSSE';
 * 
 * const MyComponent = () => {
 *   const { connected, connectionError } = useSSE({
 *     onTaskCreated: (event) => {
 *       console.log('New task created:', event);
 *       toast.success(`New task: ${event.title}`);
 *     },
 *     onTaskUpdated: (event) => {
 *       console.log('Task updated:', event);
 *     }
 *   });
 *   
 *   return <div>Connected: {connected ? 'Yes' : 'No'}</div>;
 * };
 * ```
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  notifyEvidenceEvent,
  publishEvidenceEvent,
  publishEvidenceResync,
  subscribeToEvidenceEvents,
} from '../services/evidenceEvents';
import { EVIDENCE_SSE_EVENT_TYPES, type EvidenceSseEvent } from '../types/evidence';
import { getAuthToken, handleUnauthorized, resolveApiUrl } from '../utils/fetcher';
import type { components } from '../types/api.generated';

interface TaskCreatedEvent {
  event_type: 'task.created';
  timestamp: string;
  task_id: string;
  project_id: string;
  title: string;
  assigned_to: string | null;
  created_by: string;
  status: string;
  priority: string;
}

interface TaskUpdatedEvent {
  event_type: 'task.updated';
  timestamp: string;
  task_id: string;
  project_id: string;
  title: string;
  updated_by: string;
  changes: Record<string, any>;
}

interface HeartbeatEvent {
  event_type: 'heartbeat';
  timestamp: string;
  message: string;
}

type SSEEvent = TaskCreatedEvent | TaskUpdatedEvent | HeartbeatEvent;

type SseTicketResponse = components['schemas']['SseTicketResponse'];

/**
 * Mint a single-use ticket for opening the stream.
 *
 * `EventSource` cannot set headers, so the stream's credential has to travel in the URL, and a
 * URL is logged by every hop. The bearer JWT must never be that credential; the API issues a
 * ticket that is dead after one connection or 60 seconds instead. Resolves to null when the
 * session is gone (the shared 401 handling has already redirected to login).
 */
const requestSseTicket = async (): Promise<string | null> => {
  const token = getAuthToken();
  if (!token) return null;
  const response = await fetch(resolveApiUrl('/api/v1/sse/ticket'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 401) {
    handleUnauthorized();
    return null;
  }
  if (!response.ok) {
    throw new Error(`SSE ticket request failed with status ${response.status}`);
  }
  const body = (await response.json()) as SseTicketResponse;
  return body.ticket;
};

interface UseSSEOptions {
  onTaskCreated?: (event: TaskCreatedEvent) => void;
  onTaskUpdated?: (event: TaskUpdatedEvent) => void;
  onHeartbeat?: (event: HeartbeatEvent) => void;
  onError?: (error: Event) => void;
  autoReconnect?: boolean;
  reconnectDelay?: number; // milliseconds
}

interface UseSSEReturn {
  connected: boolean;
  connectionError: string | null;
  reconnecting: boolean;
  lastEvent: SSEEvent | null;
}

/**
 * Custom React hook for SSE real-time notifications.
 * 
 * @param options - Configuration options and event handlers
 * @returns Connection state and last received event
 */
export const useSSE = (options: UseSSEOptions = {}): UseSSEReturn => {
  const {
    onTaskCreated,
    onTaskUpdated,
    onHeartbeat,
    onError,
    autoReconnect = true,
    reconnectDelay = 5000,
  } = options;

  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [lastEvent, setLastEvent] = useState<SSEEvent | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  // Bumped on unmount so a ticket request that resolves afterwards does not open a stream.
  const connectGenerationRef = useRef(0);

  const MAX_RECONNECT_ATTEMPTS = 5;

  const connect = useCallback(async () => {
    if (!getAuthToken()) {
      console.warn('SSE: No authentication token found, skipping connection');
      setConnectionError('Not authenticated');
      return;
    }

    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const scheduleReconnect = () => {
      if (autoReconnect && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        setReconnecting(true);
        console.log(
          `SSE: Reconnecting in ${reconnectDelay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`
        );
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay);
      } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        console.error('SSE: Max reconnection attempts reached');
        setReconnecting(false);
        toast.error('Lost connection to server. Please refresh the page.');
      }
    };

    const generation = connectGenerationRef.current;
    let ticket: string | null;
    try {
      // Every connection, including each reconnect, needs a fresh ticket: they are single-use.
      ticket = await requestSseTicket();
    } catch (error) {
      console.error('SSE: Could not obtain a stream ticket', error);
      setConnected(false);
      setConnectionError('Connection lost');
      scheduleReconnect();
      return;
    }
    if (generation !== connectGenerationRef.current) return; // unmounted while waiting
    if (!ticket) {
      setConnectionError('Not authenticated');
      return;
    }

    try {
      console.log('SSE: Connecting to event stream...');
      // Straight to the API host (VITE_API_URL), not the same-origin /api proxy: the Static Web
      // Apps proxy buffers responses and caps requests at 45 s, so a stream never gets through it.
      const eventSource = new EventSource(
        resolveApiUrl(`/api/v1/sse/events?ticket=${encodeURIComponent(ticket)}`)
      );
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('SSE: Connected');
        // Read before the reset below: a non-zero attempt count means this open follows a drop,
        // and every event sent while the stream was down was lost.
        const wasReconnect = reconnectAttemptsRef.current > 0;
        setConnected(true);
        setConnectionError(null);
        setReconnecting(false);
        reconnectAttemptsRef.current = 0;
        if (wasReconnect) publishEvidenceResync();
      };

      eventSource.onerror = (error) => {
        console.error('SSE: Connection error', error);
        setConnected(false);
        setConnectionError('Connection lost');

        // Call custom error handler
        if (onError) {
          onError(error);
        }

        // Close the connection
        eventSource.close();
        eventSourceRef.current = null;

        // App Service drops idle connections after roughly 230 s, so a reconnect is routine;
        // the attempt counter resets on every successful open, so only consecutive failures
        // reach the limit.
        scheduleReconnect();
      };

      // Listen for task.created events
      eventSource.addEventListener('task.created', (event: MessageEvent) => {
        try {
          const data: TaskCreatedEvent = JSON.parse(event.data);
          console.log('SSE: task.created', data);
          setLastEvent(data);

          if (onTaskCreated) {
            onTaskCreated(data);
          }
        } catch (error) {
          console.error('SSE: Error parsing task.created event', error);
        }
      });

      // Listen for task.updated events
      eventSource.addEventListener('task.updated', (event: MessageEvent) => {
        try {
          const data: TaskUpdatedEvent = JSON.parse(event.data);
          console.log('SSE: task.updated', data);
          setLastEvent(data);

          if (onTaskUpdated) {
            onTaskUpdated(data);
          }
        } catch (error) {
          console.error('SSE: Error parsing task.updated event', error);
        }
      });

      // Listen for heartbeat events
      eventSource.addEventListener('heartbeat', (event: MessageEvent) => {
        try {
          const data: HeartbeatEvent = JSON.parse(event.data);
          console.debug('SSE: heartbeat', data);
          setLastEvent(data);

          if (onHeartbeat) {
            onHeartbeat(data);
          }
        } catch (error) {
          console.error('SSE: Error parsing heartbeat event', error);
        }
      });

      // Evidence lifecycle events. EventSource has no wildcard listener, so each named event
      // is registered; they are fanned out through the evidence event bus so panels can react
      // without opening a second connection.
      EVIDENCE_SSE_EVENT_TYPES.forEach((eventType) => {
        eventSource.addEventListener(eventType, (event: MessageEvent) => {
          try {
            const data: EvidenceSseEvent = JSON.parse(event.data);
            publishEvidenceEvent({ ...data, event_type: eventType });
          } catch (error) {
            console.error(`SSE: Error parsing ${eventType} event`, error);
          }
        });
      });
    } catch (error) {
      console.error('SSE: Failed to create EventSource', error);
      setConnectionError('Failed to connect');
    }
  }, [
    onTaskCreated,
    onTaskUpdated,
    onHeartbeat,
    onError,
    autoReconnect,
    reconnectDelay,
  ]);

  // Connect on mount
  useEffect(() => {
    connect();

    // Cleanup on unmount
    return () => {
      console.log('SSE: Disconnecting...');
      connectGenerationRef.current += 1;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      setConnected(false);
    };
  }, [connect]);

  return {
    connected,
    connectionError,
    reconnecting,
    lastEvent,
  };
};

/**
 * Lightweight SSE hook with default toast notifications.
 * Perfect for adding real-time notifications to any component.
 * 
 * Usage:
 * ```tsx
 * import { useSSENotifications } from '../hooks/useSSE';
 * 
 * const MyComponent = () => {
 *   useSSENotifications();
 *   return <div>Your component content</div>;
 * };
 * ```
 */
export const useSSENotifications = () => {
  // Evidence events are already targeted at this user by the backend, so every one that
  // arrives deserves a toast. The panel dedupes its own HTTP-result toasts against these by id.
  useEffect(() => subscribeToEvidenceEvents(notifyEvidenceEvent), []);

  return useSSE({
    onTaskCreated: (event) => {
      toast.success(
        `New task created: ${event.title}`,
        {
          duration: 5000,
          icon: '📋',
        }
      );
    },
    onTaskUpdated: (event) => {
      const changeCount = Object.keys(event.changes).length;
      toast(
        `Task "${event.title}" updated (${changeCount} change${changeCount > 1 ? 's' : ''})`,
        {
          duration: 4000,
          icon: '✏️',
        }
      );
    },
  });
};

export type { TaskCreatedEvent, TaskUpdatedEvent, HeartbeatEvent, SSEEvent, UseSSEOptions };
